import React, { useEffect, useMemo, useState } from "react";
import { Table } from "reactstrap";
import { usePagination, useRowSelect, useTable } from "react-table";
import SearchIcon from "mdi-react/SearchIcon";
import ReactTablePagination from "@/shared/components/table/components/ReactTablePagination";
import ModalWrapper from "./ModalWrapper";
import apiTableService from "@/services/apiTableService";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import TemplateButton from "@/shared/components/TemplateButton";
import { formatPoints } from "@/containers/Participant/Points/components/formatPoints";

const QUERY_PAGE_SIZE = 20;

const IndeterminateCheckbox = React.forwardRef(
  ({ indeterminate, className = "", ...rest }, ref) => {
    const defaultRef = React.useRef(null);
    const resolvedRef = ref || defaultRef;

    React.useEffect(() => {
      if (resolvedRef.current) {
        resolvedRef.current.indeterminate = indeterminate;
      }
    }, [resolvedRef, indeterminate]);

    return <input type="checkbox" ref={resolvedRef} className={className} {...rest} />;
  }
);

const ProgramUsers = ({ program, organization, auth, template, pointBalance }) => {
  const { t } = useTranslation();
  const [modalName, setModalName] = useState(null);
  const [isOpen, setOpen] = useState(false);
  const [users, setUsers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [participants, setParticipants] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [debouncedKeyword, setDebouncedKeyword] = useState("");

  const factor = Number(program?.factor_valuation ?? pointBalance?.factor ?? 1);
  const peerBalancePoints = formatPoints((pointBalance?.peerBalance ?? 0) * factor);

  const toggle = (name = null) => {
    if (name) setModalName(name);
    setOpen((prevState) => !prevState);
  };

  const columns = useMemo(
    () => [
      {
        Header: "participant",
        accessor: "name",
        Cell: ({ row, value }) => {
          const participantName =
            value ||
            [row.original?.first_name, row.original?.last_name].filter(Boolean).join(" ");
          const details = [];

          if (row.original?.unitNumber?.name) {
            details.push(row.original.unitNumber.name);
          }

          if (row.original?.award_level && row.original.award_level !== "default") {
            details.push(row.original.award_level);
          }

          if (
            row.original?.status?.status &&
            row.original.status.status !== "Active"
          ) {
            details.push(row.original.status.status);
          }

          return (
            <div className="peer-award__participant">
              <span className="peer-award__participant-name">{participantName || "—"}</span>
              {details.length > 0 && (
                <div className="peer-award__participant-meta">
                  {details.map((item) => (
                    <span key={item} className="peer-award__participant-chip">
                      {item}
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        },
      },
      {
        Header: "email",
        accessor: "email",
        Cell: ({ value }) => <span className="peer-award__email">{value || "—"}</span>,
      },
    ],
    []
  );
  const totalPageCount = Math.ceil((users?.count ?? 0) / QUERY_PAGE_SIZE);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    pageOptions,
    gotoPage,
    previousPage,
    canPreviousPage,
    nextPage,
    canNextPage,
    setPageSize,
    selectedFlatRows,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data: users?.results ?? [],
      initialState: {
        pageIndex: 0,
        pageSize: QUERY_PAGE_SIZE,
      },
      manualPagination: true,
      pageCount: users ? totalPageCount : null,
      autoResetSortBy: false,
      autoResetExpanded: false,
      autoResetPage: false,
    },
    usePagination,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((visibleColumns) => [
        {
          id: "selection",
          Header: ({ getToggleAllPageRowsSelectedProps }) => (
            <div className="peer-award__checkbox-wrap" onClick={(event) => event.stopPropagation()}>
              <IndeterminateCheckbox
                {...getToggleAllPageRowsSelectedProps()}
                className="peer-award__checkbox"
              />
            </div>
          ),
          Cell: ({ row }) => (
            <div className="peer-award__checkbox-wrap" onClick={(event) => event.stopPropagation()}>
              <IndeterminateCheckbox
                {...row.getToggleRowSelectedProps()}
                className="peer-award__checkbox"
              />
            </div>
          ),
        },
        ...visibleColumns,
      ]);
    }
  );

  const selectedParticipants = useMemo(
    () => selectedFlatRows.map((item) => item.original),
    [selectedFlatRows]
  );
  const selectedCount = selectedParticipants.length;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedKeyword(keyword.trim());
    }, 250);

    return () => clearTimeout(timer);
  }, [keyword]);

  useEffect(() => {
    if (!auth?.id || !organization?.id || !program?.id) {
      return;
    }

    let mounted = true;
    setLoading(true);

    const requestFilter = {
      keyword: debouncedKeyword,
      except: [auth.id],
      show_flag: "published",
    };

    apiTableService
      .fetchData({
        url: `/organization/${organization.id}/program/${program.id}/participant`,
        page: pageIndex,
        size: pageSize,
        filter: requestFilter,
      })
      .then((items) => {
        if (!mounted) return;
        setUsers(items);
        setLoading(false);
      })
      .catch((error) => {
        console.error(error);
        if (!mounted) return;
        setUsers({ count: 0, results: [] });
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [auth?.id, debouncedKeyword, organization?.id, pageIndex, pageSize, program?.id]);

  const onClickReward = () => {
    if (selectedParticipants.length === 0) {
      alert(t("select_participants"));
      return;
    }

    setParticipants(selectedParticipants);
    toggle("Reward");
  };

  const onKeywordChange = (event) => {
    setKeyword(event.target.value);
    gotoPage(0);
  };

  const renderSelectedNames = () => {
    if (selectedCount === 0) return null;

    return (
      <div className="peer-award__selected-strip">
        {selectedParticipants.slice(0, 4).map((item) => (
          <span key={item.id} className="peer-award__selected-chip">
            {item.name}
          </span>
        ))}
        {selectedCount > 4 && (
          <span className="peer-award__selected-chip peer-award__selected-chip--muted">
            +{selectedCount - 4}
          </span>
        )}
      </div>
    );
  };

  const renderTable = () => {
    if (loading) {
      return (
        <div className="peer-award__state">
          <p>{t("loading")}</p>
        </div>
      );
    }

    if ((users?.results?.length ?? 0) === 0) {
      return (
        <div className="peer-award__state">
          <p>{t("no_participants_found")}</p>
        </div>
      );
    }

    return (
      <div className="peer-award__table-wrap">
        <Table striped borderless size="md" className="peer-award__table table--stack-mobile" {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th {...column.getHeaderProps()}>
                    {column.id === "selection" ? column.render("Header") : t(column.Header)}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              return (
                <tr
                  {...row.getRowProps()}
                  className={row.isSelected ? "is-selected" : ""}
                >
                  {row.cells.map((cell) => (
                    <td
                      {...cell.getCellProps()}
                      data-label={typeof cell.column.Header === "string" ? t(cell.column.Header) : ""}
                    >
                      {cell.render("Cell")}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    );
  };

  return (
    <>
      <div className="peer-award">
        <div className="peer-award__summary">
          <div className="peer-award__summary-card">
            <span className="peer-award__summary-label">{t("peer_points_to_award")}</span>
            <strong className="peer-award__summary-value">
              {peerBalancePoints} {t("points")}
            </strong>
          </div>
          <div className="peer-award__summary-card">
            <span className="peer-award__summary-label">{t("selected_participants")}</span>
            <strong className="peer-award__summary-value">{selectedCount}</strong>
          </div>
        </div>

        <div className="peer-award__panel">
          <div className="peer-award__toolbar">
            <label className="peer-award__search" aria-label={t("search_participants")}>
              <SearchIcon size={18} />
              <input
                type="search"
                value={keyword}
                onChange={onKeywordChange}
                placeholder={t("search_participants")}
              />
            </label>

            <div className="peer-award__toolbar-actions">
              <TemplateButton
                onClick={onClickReward}
                text={t("reward")}
                disabled={selectedCount === 0}
                className="peer-award__cta"
              />
            </div>
          </div>

          {selectedCount > 0 && (
            <div className="peer-award__selection-note">
              {selectedCount} {t("selected_participants")}
            </div>
          )}
          {renderSelectedNames()}
          {renderTable()}
        </div>

        {!loading && (users?.results?.length ?? 0) > 0 && (
          <div className="peer-award__pagination">
            <ReactTablePagination
              page={page}
              gotoPage={gotoPage}
              previousPage={previousPage}
              nextPage={nextPage}
              canPreviousPage={canPreviousPage}
              canNextPage={canNextPage}
              pageOptions={pageOptions}
              pageSize={pageSize}
              pageIndex={pageIndex}
              setPageSize={setPageSize}
              manualPageSize={[]}
              dataLength={users?.count ?? 0}
            />
          </div>
        )}
      </div>

      <ModalWrapper
        name={modalName}
        isOpen={isOpen}
        setOpen={setOpen}
        toggle={toggle}
        participants={participants}
      />
    </>
  );
};

const mapStateToProps = (state) => ({
  auth: state.auth,
  template: state.template,
});

export default connect(mapStateToProps)(ProgramUsers);
